import React, { useCallback } from 'react';
import { Button, Tooltip } from 'antd';
import { MentionsInput, Mention } from 'react-mentions';
import { compareStrings, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { UserName } from '../name_component/user/UserNameComponent';
import { AudioOutlined } from '@ant-design/icons';
import AudioRecorder from '../audio_recorder/AudioRecorder';
import NoteTypingDetailDisplay from '../buyer_new_work_order_note_form/NoteTypingDetailDisplay';

interface VoiceNoteRecorderProps {
	value: any;
	typingDetails: any[];
	onChange: (value: any) => void;
	onTyping: () => void;
	handleSubmit: (e: any) => void;
	onSearch: (search: string, cb: (results: any[]) => void) => void;
	onSendAudio?: (blob: Blob, file: File) => void;
	inputPlaceholder?: string;
	inputContainerClassName?: string;
	micContainerClassName?: string;
	hideTypingDetails?: boolean;
	inputClassName?: string;
	micButtonClassName?: string;
	suggestionsPortalHost?: Element;
	/** Ref to the textarea's container box — exposed so callers can measure its width/position (e.g. to
	 *  align a referenced-task banner rendered above the input). */
	inputContainerRef?: React.Ref<HTMLDivElement>;
}

const VoiceNoteRecorder = ({
	value,
	typingDetails,
	onChange,
	onTyping,
	handleSubmit,
	onSearch,
	onSendAudio,
	inputPlaceholder = 'Add a note to this work order.',
	inputContainerClassName = 'w-full',
	micContainerClassName = 'mb-7 flex h-[40px] items-center justify-center bg-zinc-50 px-3',
	hideTypingDetails = false,
	inputClassName = 'border border-solid border-black',
	micButtonClassName = '',
	suggestionsPortalHost,
	inputContainerRef,
}: VoiceNoteRecorderProps) => {
	const hasText = value && value.trim && value.trim().length > 0;

	const handleAudioSend = useCallback(
		(blob: Blob, file: File) => {
			if (onSendAudio) onSendAudio(blob, file);
		},
		[onSendAudio]
	);

	return (
		<>
			<div ref={inputContainerRef} className={inputContainerClassName}>
				<MentionsInput
					placeholder={inputPlaceholder}
					forceSuggestionsAboveCursor
					suggestionsPortalHost={suggestionsPortalHost}
					value={value}
					onChange={(e, newValue) => {
						onChange(newValue);
					}}
					style={{
						...(inputClassName.includes('border') ? {} : { border: 'none' }),
						input: { border: 'none', outline: 'none', boxShadow: 'none' },
						suggestions: { zIndex: 1050 },
					}}
					className={`chat-input ant-input ${inputClassName}`}
					allowSpaceInQuery={true}
					onKeyDown={(e) => {
						onTyping();
						if (e.key === 'Enter' && !e.shiftKey) {
							stopDefaultEvents(e);
							handleSubmit(e);
						}
					}}
				>
					<Mention
						trigger="@"
						data={onSearch}
						className="bg-[#d2d9de]"
						renderSuggestion={(user, search, highlightedDisplay, index, focused) => {
							const isInactive = compareStrings(user.status, 'inactive');
							return (
								<div
									className={
										'flex flex-row items-center p-2 px-4 ' +
										(isInactive ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')
									}
									onClick={(e) => {
										if (isInactive) {
											e.preventDefault();
											e.stopPropagation();
										}
									}}
								>
									<UserName
										contact={user}
										onClick={() => {}}
										status={user.status === 'not_available' ? undefined : user.status}
									/>
								</div>
							);
						}}
					/>
				</MentionsInput>
				{!hideTypingDetails && (
					<NoteTypingDetailDisplay typingDetails={typingDetails} textValue={value} />
				)}
			</div>
			<div className={micContainerClassName}>
				{!hasText ? (
					<AudioRecorder
						containerType="popover"
						popoverPlacement="topRight"
						onSend={handleAudioSend}
						sendLabel="Send"
						modalTitle="Send Audionote"
					>
						<Tooltip title="Record voice note">
							<Button
								shape="circle"
								style={micButtonClassName ? undefined : { border: 'none' }}
								className={micButtonClassName}
								icon={
									<AudioOutlined
										className="stroke-character-secondary text-character-secondary"
										style={{ fontSize: 20 }}
									/>
								}
							/>
						</Tooltip>
					</AudioRecorder>
				) : (
					<Tooltip title="Send">
						<Button
							htmlType="submit"
							shape="circle"
							style={micButtonClassName ? undefined : { border: 'none' }}
							className={micButtonClassName}
						>
							<i style={{ fontSize: 20 }} className="icons8-font icons8-paper-airplane"></i>
						</Button>
					</Tooltip>
				)}
			</div>
		</>
	);
};

export default VoiceNoteRecorder;
